/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { bind } from 'bind-event-listener';

import { getDocument } from '@atlaskit/browser-apis';
import { css, jsx } from '@atlaskit/css';
import { BLOCK_CONTROL_UI_CONTEXT } from '@atlaskit/editor-common/block-controls/block-control-ui-context';
import { BLOCK_CONTROLS_LEFT_SURFACE } from '@atlaskit/editor-common/block-controls/surface-keys';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import {
	SurfaceRenderer,
	willSurfaceRender,
} from '@atlaskit/editor-ui-control-model/surface-renderer';
import { createSurfaceContext } from '@atlaskit/editor-ui-control-model/types';

import type { BlockControlsPlugin } from '../blockControlsPluginType';
import { getNodeTypeWithLevel } from '../pm-plugins/decorations-common';

import { createBlockControlsSurfaceContext } from './block-controls-surface-context';
import { getNodeContentElement } from './utils/get-node-content-element';
import {
	getAbsoluteSurfacePlacement,
	getSurfacePlacement,
	type SurfacePlacement,
} from './utils/get-surface-placement';
import { VisibilityContainer } from './visibility-container';

const BLOCK_CONTROLS_SURFACE_Z_INDEX = 100;

const surfaceStyles = css({
	display: 'flex',
	position: 'absolute',
	zIndex: BLOCK_CONTROLS_SURFACE_Z_INDEX,
});

type Props = {
	api: ExtractInjectionAPI<BlockControlsPlugin>;
	editorView: EditorView;
};

export const BlockControlsSurface = ({ api, editorView }: Props): React.JSX.Element | null => {
	const activeNode = useSharedPluginStateWithSelector(api, ['blockControls'], (states) => ({
		activeNode: states.blockControlsState?.activeNode,
	})).activeNode;
	const [position, setPosition] = useState<SurfacePlacement>();
	const animationFrameRef = useRef<number | undefined>(undefined);

	const components =
		api.uiControlRegistry?.actions.getComponents(BLOCK_CONTROLS_LEFT_SURFACE) ?? [];
	const blockControlsContext = useMemo(
		() => createBlockControlsSurfaceContext(editorView, activeNode),
		[activeNode, editorView],
	);
	const surfaceContext = useMemo(
		() =>
			blockControlsContext
				? createSurfaceContext(BLOCK_CONTROL_UI_CONTEXT, blockControlsContext)
				: undefined,
		[blockControlsContext],
	);

	useLayoutEffect(() => {
		if (!blockControlsContext) {
			setPosition(undefined);
			return;
		}

		const rootNodeElement = editorView.nodeDOM(blockControlsContext.rootNode.pos);
		const rootElement =
			rootNodeElement instanceof HTMLElement ? rootNodeElement : rootNodeElement?.parentElement;
		if (!rootElement) {
			setPosition(undefined);
			return;
		}
		const targetElement = getNodeContentElement(
			rootElement,
			blockControlsContext.rootNode.type.name,
		);
		const offsetParent = editorView.dom.offsetParent;
		const isDocumentOffsetParent = !offsetParent || offsetParent === getDocument()?.body;
		const scrollingOffsetParent =
			offsetParent instanceof HTMLElement && !isDocumentOffsetParent ? offsetParent : undefined;

		const updatePosition = () => {
			animationFrameRef.current = undefined;
			if (!rootElement.isConnected || !targetElement.isConnected) {
				setPosition(undefined);
				return;
			}
			const placement = getSurfacePlacement({
				layout: rootElement.getAttribute('layout') ?? '',
				nodeRect: targetElement.getBoundingClientRect(),
				nodeType: blockControlsContext.rootNode.type.name,
				nodeTypeWithLevel: getNodeTypeWithLevel(blockControlsContext.rootNode.node),
				parentNodeType:
					blockControlsContext.rootNode.parentType === 'doc'
						? undefined
						: blockControlsContext.rootNode.parentType,
			});
			const offsetParentRect = offsetParent?.getBoundingClientRect();
			const offsetParentScrollTop = scrollingOffsetParent?.scrollTop ?? window.scrollY;
			const offsetParentScrollLeft = scrollingOffsetParent?.scrollLeft ?? window.scrollX;
			setPosition(
				getAbsoluteSurfacePlacement({
					offsetParentRect,
					offsetParentScrollLeft,
					offsetParentScrollTop,
					placement,
				}),
			);
		};
		const schedulePositionUpdate = () => {
			if (animationFrameRef.current === undefined) {
				animationFrameRef.current = requestAnimationFrame(updatePosition);
			}
		};

		updatePosition();
		const resizeObserver = new ResizeObserver(schedulePositionUpdate);
		resizeObserver.observe(editorView.dom);
		if (targetElement && targetElement !== editorView.dom) {
			resizeObserver.observe(targetElement);
		}
		if (offsetParent instanceof HTMLElement && offsetParent !== editorView.dom) {
			resizeObserver.observe(offsetParent);
		}
		const unbindWindowResize = bind(window, {
			type: 'resize',
			listener: schedulePositionUpdate,
		});
		const unbindWindowScroll = bind(window, {
			type: 'scroll',
			listener: schedulePositionUpdate,
			options: { capture: true, passive: true },
		});
		const visualViewport = window.visualViewport;
		const unbindVisualViewportResize = visualViewport
			? bind(visualViewport, { type: 'resize', listener: schedulePositionUpdate })
			: undefined;
		const unbindVisualViewportScroll = visualViewport
			? bind(visualViewport, { type: 'scroll', listener: schedulePositionUpdate })
			: undefined;
		const unbindTransitionEnd = bind(targetElement, {
			type: 'transitionend',
			listener: schedulePositionUpdate,
		});

		return () => {
			if (animationFrameRef.current !== undefined) {
				cancelAnimationFrame(animationFrameRef.current);
				animationFrameRef.current = undefined;
			}
			resizeObserver.disconnect();
			unbindWindowResize();
			unbindWindowScroll();
			unbindVisualViewportResize?.();
			unbindVisualViewportScroll?.();
			unbindTransitionEnd();
		};
	}, [blockControlsContext, editorView]);

	if (
		components.length === 0 ||
		!blockControlsContext ||
		!surfaceContext ||
		!position ||
		!willSurfaceRender(components, BLOCK_CONTROLS_LEFT_SURFACE, surfaceContext)
	) {
		return null;
	}

	return (
		<div
			css={surfaceStyles}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Placement is resolved from the active node's runtime DOM geometry.
			style={position}
			data-testid="block-controls-left-surface"
		>
			<VisibilityContainer api={api} controlSide="left" shouldUseDisplayContents>
				<SurfaceRenderer
					components={components}
					surface={BLOCK_CONTROLS_LEFT_SURFACE}
					surfaceContext={surfaceContext}
				/>
			</VisibilityContainer>
		</div>
	);
};
